//CPE: Claim Processing Engine

import Axios, { AxiosInstance, AxiosStatic } from 'axios';
import { fetchCPEDataExtract } from '@context/inspections';
import { CPE_JWT } from '@shared/api/config';
import { ACTIVE_CPE } from '@shared/utils';
import { Validation } from '@/types/action';
import { CountryCode } from '@/types/common';
import { PhotoSection } from '@/types/images';
import { FieldSection } from '@/types/section-fields';

const CPE_API = import.meta.env.VITE_CPE_BASE_URL;

export interface ClaimResponse {
  id: string;
  policyId: string;
  claimNumber: string;
  company: string;
  companyId: string;
  driverId: string;
  insuredIndividualId: string;
  driverRelationshipToInsured: string;
  claimFiler: string;
  claimBeneficiary: string;
  createdAt: string;
  updatedAt: string;
  completedAt: string;
  currentAgentId: string;
  status: string;
  currentStage: string;
  policyType: string;
  additionalNotes: string;
  poNumber: string;
}

interface ClaimInspectionResponse {
  id: string;
  claimId: string;
}

interface UploadedStatus {
  type: string;
  description: string;
}

interface StatusFiles {
  uploaded: UploadedStatus[];
  pending: string[];
}

interface ClaimDocumentStatusResponse {
  vehicleImage: StatusFiles;
  documents: StatusFiles;
}

interface ClaimAdditionalDocumentsResponse {
  id: string;
  claimId: string;
  documentType: string;
}

export interface CategoryLicensePlateResults {
  detection: {
    detected: boolean;
    boundingBox: Record<string, unknown> | null;
    confidence: number | null;
  };
  licensePlateNumber: string | null;
  metadata: {
    averageOcrConfidence: number;
    processedAt: string;
    timings: {
      imageProcessing: number;
      objectDetection: number;
      imageAugmentation: number;
      imageCropping: number;
      ocrProcessing: number;
      textAnalysis: number;
      totalProcessingTime: number;
    };
  };
}

export interface CategoryDriverLicenseResults {
  licenseNumber: string | null;
  fullName: string | null;
  dateOfBirth: string | null;
  residentialAddress: string | null;
  issueDate: string | null;
  expirationDate: string | null;
  issuingTerritory: string | null;
  metadata: {
    averageOcrConfidence: number;
    processedAt: string;
    timings: {
      imageProcessing: number;
      objectDetection: number;
      imageCropping: number;
      imageAugmentation: number;
      ocrProcessing: number;
      bedrockAnalysis: number;
      totalProcessingTime: number;
    };
  };
}

export interface CategoryVehicleResults {
  angle: string; // Detected vehicle angle
  probability: number; // Model confidence (0-100)
  metadata: {
    processedAt: string;
    timings: {
      imageProcessing: number;
      predictionProcessing: number;
      totalProcessingTime: number;
    };
  };
}

export interface CategoryVINResults {
  vinNumber: string | null;
  metadata: {
    averageOcrConfidence: number;
    processedAt: string;
    timings: {
      imageProcessing: number;
      grayscaleProcessing: number;
      ocrProcessing: number;
      textAnalysis: number;
      totalProcessingTime: number;
    };
  };
}

export interface CategoryMarbeteResults {
  marbeteNumber: string | null;
  metadata: {
    averageOcrConfidence: number;
    processedAt: string;
    timings: {
      imageProcessing: number;
      grayscaleProcessing: number;
      ocrProcessing: number;
      textAnalysis: number;
      totalProcessingTime: number;
    };
  };
}

export interface CategoryCarRegistrationResults {
  fullName: string;
  registrationNumber: string;
  issueDate: string | null;
  licensePlate: string;
  titleNumber: string | null;
  make: string | null;
  model: string | null;
  year: string | null;
  color: string | null;
  vin: string;
  effectiveDate: string;
  expirationDate: string;
  marbeteNumber: string | null;
  isProvisional: boolean;
  metadata: {
    averageOcrConfidence: number;
    processedAt: string;
    timings: {
      imageProcessing: number;
      imageAugmentation: number;
      ocrProcessing: number;
      textCleaning: number;
      bedrockAnalysis: number;
      resultProcessing: number;
      totalProcessingTime: number;
    };
  };
}

export interface UploadImageResponse {
  id: string;
  filename: string;
  claimId: string;
  inspectionId: string | null;
  s3Link: string;
  category: string;
  mlResults?: any;
}

export enum Categories {
  DRIVER_LICENSE = 'drivers_license',
  LICENSE_PLATE = 'license_plate',
  CAR_REGISTRATION = 'car_registration',
  VEHICLE = 'vehicle',
  ODOMETER = 'odometer',
  VIN_NUMBER = 'vin_number',
  VEHICLE_DAMAGE = 'vehicle_damage',
  VEHICLE_TRUNK = 'vehicle_trunk',
  VEHICLE_HOOD = 'vehicle_hood',
  VEHICLE_INTERIOR = 'vehicle_interior',
  MARBETE = 'marbete',
  ADDITIONAL_DOCUMENTS = 'additional_documents',
  ADDITIONAL_DRIVERS_LICENSE = 'additional_drivers_license',
  BANK_STATEMENT = 'bank_statement',
  BILL_OF_SALE = 'bill_of_sale',
  BIRTH_CERTIFICATE = 'birth_certificate',
  OTHER = 'other',
}

export enum Angles {
  left = 'Left',
  right = 'Right',
  front = 'Front',
  rear = 'Rear',
}

export interface CPESyncData {
  ownerLicenseData: string | null;
  ownerNameData: string | null;
  ownerLastNameData: string | null;
  ownerAddress1Data: string | null;
  ownerAddress2Data: string | null;
  ownerAddressTownData: string | null;
  ownerAddressStateData: string | null;
  driverLicenseData: string | null;
  driverNameData: string | null;
  driverLastNameData: string | null;
  driverAddress1Data: string | null;
  driverAddress2Data: string | null;
  driverAddressTownData: string | null;
  driverAddressStateData: string | null;
  vehicleBrandData: string | null;
  vehicleModelData: string | null;
  vehicleYearData: string | null;
  vehicleColorData: string | null;
  vehicleVinData: string | null;
  vehicleRegistrationData: string | null;
  vehiclePlateData: string | null;
  vehicleMarbeteEffectiveDate: string | null;
  vehicleMarbeteExpirationDate: string | null;
}

export const validationMapping: Record<any, { category: Categories }> = {
  [Validation.HAS_VEHICLE]: { category: Categories.VEHICLE },
  [Validation.EXTRACT_PLATE]: { category: Categories.LICENSE_PLATE },
  [Validation.DRIVER_LICENSE]: { category: Categories.DRIVER_LICENSE },
  [Validation.MARBETE]: { category: Categories.MARBETE },
  [Validation.EXTRACT_VIN]: { category: Categories.VIN_NUMBER },
  [Validation.EXTRACT_CAR_REGISTRATION]: {
    category: Categories.CAR_REGISTRATION,
  },
  [Validation.PUSH_ODOMETER]: { category: Categories.ODOMETER },
  [Validation.PUSH_VEHICLE_DAMAGE]: { category: Categories.VEHICLE_DAMAGE },
  [Validation.ADDITIONAL_DRIVERS_LICENSE]: {
    category: Categories.ADDITIONAL_DRIVERS_LICENSE,
  },
  [Validation.BANK_STATEMENT]: {
    category: Categories.BANK_STATEMENT,
  },
  [Validation.BILL_OF_SALE]: {
    category: Categories.BILL_OF_SALE,
  },
  [Validation.BIRTH_CERTIFICATE]: {
    category: Categories.BIRTH_CERTIFICATE,
  },
};

export const checkPhotoSections = (photoSection: PhotoSection): string => {
  switch (photoSection) {
    case PhotoSection.INSURED_LICENSE:
      return 'DRIVER_LICENSE';
    case PhotoSection.INSURED_VEHICLE_LICENSE:
      return 'EXTRACT_CAR_REGISTRATION';
    case PhotoSection.INSURED_MARBETE:
      return 'MARBETE';
    case PhotoSection.INSURED_VIN:
      return 'EXTRACT_VIN';
    case PhotoSection.INSURED_TABLILLA:
      return 'EXTRACT_PLATE';
    case PhotoSection.INSURED_VEH_DAMAGE_PHOTOS:
      return 'PUSH_VEHICLE_DAMAGE';
    case PhotoSection.INSURED_VEH_FRONTSIDE_PHOTO:
      return 'HAS_VEHICLE';
    case PhotoSection.INSURED_VEH_REARSIDE_PHOTO:
      return 'HAS_VEHICLE';
    case PhotoSection.INSURED_VEH_LEFT_SIDE_PHOTO:
      return 'HAS_VEHICLE';
    case PhotoSection.INSURED_VEH_RIGHT_SIDE_PHOTO:
      return 'HAS_VEHICLE';
    case PhotoSection.INJURED_VEH_FRONTSIDE_PHOTO:
      return 'HAS_VEHICLE';
    case PhotoSection.INJURED_VEH_REARSIDE_PHOTO:
      return 'HAS_VEHICLE';
    case PhotoSection.INJURED_VEH_LEFT_SIDE_PHOTO:
      return 'HAS_VEHICLE';
    case PhotoSection.INJURED_VEH_RIGHT_SIDE_PHOTO:
      return 'HAS_VEHICLE';
    case PhotoSection.INSURED_ODOMETER:
      return 'PUSH_ODOMETER';
    case PhotoSection.INSURED_ADDITIONAL_DRIVER_LICENSE:
      return 'ADDITIONAL_DRIVERS_LICENSE';
    case PhotoSection.INSURED_BANK_STATEMENT:
      return 'BANK_STATEMENT';
    case PhotoSection.INSURED_BILL_OF_SALE:
      return 'BILL_OF_SALE';
    case PhotoSection.INSURED_BIRTH_CERTIFICATE:
      return 'BIRTH_CERTIFICATE';
    case PhotoSection.INJURED_LICENSE:
      return 'DRIVER_LICENSE';
    case PhotoSection.INJURED_VEHICLE_LICENSE:
      return 'EXTRACT_CAR_REGISTRATION';
    case PhotoSection.INJURED_MARBETE:
      return 'MARBETE';
    case PhotoSection.INJURED_VIN:
      return 'EXTRACT_VIN';
    case PhotoSection.INJURED_TABLILLA:
      return 'EXTRACT_PLATE';
    case PhotoSection.INJURED_VEH_DAMAGE_PHOTOS:
      return 'PUSH_VEHICLE_DAMAGE';
    case PhotoSection.INJURED_ODOMETER:
      return 'PUSH_ODOMETER';
    case PhotoSection.INJURED_ADDITIONAL_DRIVER_LICENSE:
      return 'ADDITIONAL_DRIVERS_LICENSE';
    case PhotoSection.INJURED_BANK_STATEMENT:
      return 'BANK_STATEMENT';
    case PhotoSection.INJURED_BILL_OF_SALE:
      return 'BILL_OF_SALE';
    case PhotoSection.INJURED_BIRTH_CERTIFICATE:
      return 'BIRTH_CERTIFICATE';
    default:
      return 'UNDEFINED';
  }
};

export const parseResultsCpe = async (
  photoSectionId: PhotoSection,
  category: Categories,
  result: UploadImageResponse,
  currentSetSections?: (sections: FieldSection[]) => void,
  currentSections?: FieldSection[],
  claimantType?: string,
): Promise<boolean> => {
  if (!result?.mlResults) {
    return true;
  }

  switch (category) {
    case Categories.LICENSE_PLATE: {
      const plateResults: CategoryLicensePlateResults = JSON.parse(
        result.mlResults,
      );
      console.warn('Parsed License Plate Results:', plateResults);
      await fetchCPEDataExtract(
        photoSectionId,
        plateResults,
        currentSetSections,
        currentSections,
      );
      break;
    }
    case Categories.CAR_REGISTRATION: {
      const carResults: CategoryCarRegistrationResults = JSON.parse(
        result.mlResults,
      );
      console.warn('Parsed Car Registration Results:', carResults);
      await fetchCPEDataExtract(
        photoSectionId,
        carResults,
        currentSetSections,
        currentSections,
        claimantType,
      );
      break;
    }
    case Categories.DRIVER_LICENSE: {
      const licenseResults: CategoryDriverLicenseResults = JSON.parse(
        result.mlResults,
      );
      console.warn('Parsed Driver License Results:', licenseResults);
      await fetchCPEDataExtract(
        photoSectionId,
        licenseResults,
        currentSetSections,
        currentSections,
        claimantType,
      );
      break;
    }
    case Categories.ADDITIONAL_DRIVERS_LICENSE: {
      const licenseResults: CategoryDriverLicenseResults = JSON.parse(
        result.mlResults,
      );
      console.warn('Parsed Additional Driver License Results:', licenseResults);
      await fetchCPEDataExtract(
        photoSectionId,
        licenseResults,
        currentSetSections,
        currentSections,
        claimantType,
      );
      break;
    }
    case Categories.MARBETE: {
      const marbeteResults: CategoryMarbeteResults = JSON.parse(
        result.mlResults,
      );
      console.warn('Parsed Marbete Results:', marbeteResults);
      await fetchCPEDataExtract(
        photoSectionId,
        marbeteResults,
        currentSetSections,
        currentSections,
        claimantType,
      );
      break;
    }
    case Categories.VIN_NUMBER: {
      const vinResults: CategoryVINResults = JSON.parse(result.mlResults);
      console.warn('Parsed VIN Results:', vinResults);
      await fetchCPEDataExtract(
        photoSectionId,
        vinResults,
        currentSetSections,
        currentSections,
        claimantType,
      );
      break;
    }
    case Categories.VEHICLE: {
      const angleResults: CategoryVehicleResults = JSON.parse(result.mlResults);
      console.warn('Parsed Angle Results:', angleResults);

      if (
        photoSectionId === PhotoSection.INSURED_VEH_FRONTSIDE_PHOTO ||
        photoSectionId === PhotoSection.INJURED_VEH_FRONTSIDE_PHOTO
      ) {
        return angleResults.angle === Angles.front;
      }

      if (
        photoSectionId === PhotoSection.INSURED_VEH_REARSIDE_PHOTO ||
        photoSectionId === PhotoSection.INJURED_VEH_REARSIDE_PHOTO
      ) {
        return angleResults.angle === Angles.rear;
      }

      if (
        photoSectionId === PhotoSection.INSURED_VEH_LEFT_SIDE_PHOTO ||
        photoSectionId === PhotoSection.INJURED_VEH_LEFT_SIDE_PHOTO
      ) {
        return angleResults.angle === Angles.left;
      }

      if (
        photoSectionId === PhotoSection.INSURED_VEH_RIGHT_SIDE_PHOTO ||
        photoSectionId === PhotoSection.INJURED_VEH_RIGHT_SIDE_PHOTO
      ) {
        return angleResults.angle === Angles.right;
      }

      break;
    }
    default: {
      console.warn('Unknown category for parsing', category);
      break;
    }
  }

  return true;
};

export const isActiveIntegrationCpe = (): boolean => {
  const countryCode = localStorage.getItem('countryCode');

  return countryCode === CountryCode.PRI && ACTIVE_CPE;
};

export default class CpeClient {
  private apiClient: AxiosInstance;

  getJwt(): string {
    const cognitoIdToken = localStorage.getItem('idToken');

    if (cognitoIdToken) {
      return cognitoIdToken;
    } else if (CPE_JWT) {
      return CPE_JWT;
    } else {
      throw new Error(
        'No CPE JWT token found. Check "idToken" in localStorage or "CPE_JWT" in environment variables',
      );
    }
  }

  constructor(private axios: AxiosStatic = Axios) {
    this.apiClient = this.axios.create({
      baseURL: CPE_API,
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${this.getJwt()}`,
      },
    });
  }

  async getClaim(poNumber: string): Promise<ClaimResponse[]> {
    const response = await this.apiClient.get(
      `/v1/claims?poNumber=${poNumber}`,
    );

    return response.data;
  }

  async getInspection(claimId: string): Promise<ClaimInspectionResponse[]> {
    const response = await this.apiClient.get(
      `/v1/claims/${claimId}/inspections`,
    );

    return response.data;
  }

  async getDocumentsStatus(
    claimId: string,
  ): Promise<ClaimDocumentStatusResponse> {
    const response = await this.apiClient.get(
      `/v1/claims/${claimId}/documents/status`,
    );

    return response.data;
  }

  async getAdditionalDocuments(
    claimId: string,
  ): Promise<ClaimAdditionalDocumentsResponse[]> {
    const response = await this.apiClient.get(
      `/v1/claims/${claimId}/additionalDocuments`,
    );

    return response.data;
  }

  async sendImageValidation(
    images: File[],
    claimId: string,
    category: Categories | string,
    forceUpload: boolean,
  ): Promise<UploadImageResponse> {
    const formData = new FormData();
    images.forEach((image) => formData.append('images', image));
    formData.append('category', category);

    const endpoint = forceUpload
      ? `/v1/claims/${claimId}/images?forceUpload=true`
      : `/v1/claims/${claimId}/images`;

    const response = await this.apiClient.post(endpoint, formData, {
      headers: { 'content-type': 'multipart/form-data' },
    });

    return response.data;
  }

  async deleteImage(claimId: string, imageId: string): Promise<any> {
    const response = await this.apiClient.delete(
      `/v1/claims/${claimId}/images/${imageId}`,
    );

    return response.data;
  }

  async completeInspection(
    claimId: string,
    inspectionId: string,
  ): Promise<any> {
    try {
      const response = await this.apiClient.patch(
        `/v1/claims/${claimId}/inspections/${inspectionId}`,
        {
          inspectionDate: new Date().toISOString(),
          status: 'completed',
        },
      );

      return response.data;
    } catch (error) {
      console.error('Error:', error);

      return null;
    }
  }
}
